/**
 * Demo mode — a deterministic, scripted, fully offline run (~3 minutes) that
 * reaches a drift map without improvisation. This is what goes on a panel
 * member's phone. Never improvise in front of a jury (TECH_PLAN §4).
 *
 * It auto-drives the engine: it dwells on a seeded rotation of live fragments and
 * steps through a regime schedule, so the field visibly evolves even if the
 * viewer never moves the device. It coexists with the runtime's own timers.
 */

import { makeRng, type RegimeName } from '@wintermute/orchestrator';
import { runtime } from '../session/runtime.js';

interface RegimeStep {
  atSeconds: number;
  regime: RegimeName;
}

const SCHEDULE: RegimeStep[] = [
  { atSeconds: 0, regime: 'crisis-swarm' },
  { atSeconds: 45, regime: 'echo-well' },
  { atSeconds: 95, regime: 'memory-vault' },
  { atSeconds: 140, regime: 'comfort' },
];

const TOTAL_SECONDS = 180;

export interface DemoHandle {
  stop: () => void;
}

export function runDemo(onFinish: () => void): DemoHandle {
  const rng = makeRng(42);
  let elapsed = 0;
  let scheduleIdx = 0;

  const interval = window.setInterval(() => {
    elapsed += 1;
    const engine = runtime.engine;
    if (!engine) return;

    // Apply regime schedule.
    while (scheduleIdx < SCHEDULE.length && elapsed >= SCHEDULE[scheduleIdx]!.atSeconds) {
      runtime.setRegime(SCHEDULE[scheduleIdx]!.regime);
      scheduleIdx++;
    }

    // Auto-dwell: hold one or two live fragments this second, biased toward a
    // couple of tactics so the drift map has a clear shape.
    const live = engine.live();
    if (live.length > 0) {
      const primary = rng.pick(live);
      engine.dwell(primary.id, 0.9);
      if (rng.next() > 0.5) {
        const secondary = rng.pick(live);
        engine.dwell(secondary.id, 0.5);
      }
    }

    if (elapsed >= TOTAL_SECONDS) {
      window.clearInterval(interval);
      runtime.finish();
      onFinish();
    }
  }, 1000);

  return {
    stop: () => window.clearInterval(interval),
  };
}
